import { sortBy } from "lodash";
import React, { useContext, useMemo, useState } from "react";
import styled, { css } from "styled-components";

import { filterMatches, isUserRole } from "@examspark/common";
import {
  decomposeResourceLocation,
  NoResults,
  ResourceLocation,
  ResourceWithLocations,
  useMutationDeleteResource,
  useQueryCheckSharability,
  useQuerySearchResourcesForLocation,
  useAppContext,
} from "@examspark/common-app";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  FlexBox,
  ListTable,
  ListTableHeaderLine,
  RouterLink,
  TextInput,
  Toolbar,
  Tooltip,
  VSpace,
} from "@examspark/common-ui";
import {
  IconAddLine,
  IconDocumentLine,
  IconRefreshLine,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import {
  isClassroom,
  isCourse,
  isUnit,
  ParsedClassroom,
  ResourceVisibilityType,
  WorkspaceUserRole,
} from "@examspark/types";

import { getBrowseForItem, getVisibilityDescription } from "../../utils";
import PinToPlannerButton from "../classroom/PinToPlannerButton";
import { WindowContext } from "../common/AppWrapper";
import FavoriteButton from "../common/FavoriteButton";
import Loading from "../common/Loading";

import NewLinkModal from "./NewLinkModal";
import UploadFileModal from "./UploadFileModal";
import VisibilityModal from "./VisibilityModal";

const Results = styled(FlexBox)`
  gap: 2rem;
`;

const ResourceTools = styled.td`
  width: 0;
`;

const StyledTable = styled(ListTable)<{ mobile: boolean }>`
  ${(p) =>
    !p.mobile &&
    css`
      td:not(:first-child) {
        white-space: nowrap;
      }
    `}

  tbody td {
    vertical-align: top;
    &:not(${ResourceTools}) {
      padding-top: 1.1rem;
      padding-bottom: 1.1rem;
    }
  }
`;

const StyledToolbar = styled(Toolbar)`
  gap: 0;
`;

const StyledTooltipWrapper = styled.div`
  margin-right: 1rem;
  display: inline-block;
  position: relative;
`;

const getLocationName = (location: ResourceLocation) => {
  if (isUnit(location)) return `Unit / Module ${location.numberLabel ?? "??"}`;
  if (isCourse(location)) return "Course";
  if (isClassroom(location)) {
    if (location.resourceUnit != null) {
      return `Classroom - Unit / Module ${location.resourceUnit?.numberLabel}`;
    }
    return "Classroom";
  }
  return undefined;
};

const ResourcesTableItem = ({
  item,
  isAdmin,
  classroom,
}: {
  item: ResourceWithLocations;
  isAdmin: boolean;
  classroom: ParsedClassroom | undefined;
}) => {
  const [isVisibilityModalOpen, setIsVisibilityModalOpen] = useState(false);
  const [context] = useAppContext();
  const deleteResource = useMutationDeleteResource();

  const {
    label: typeLabel,
    url,
    IconComponent,
  } = getBrowseForItem(item.resource);

  const { label: visibilityLabel, IconComponent: VisibilityIcon } =
    getVisibilityDescription(item.resource.visibility ?? undefined);

  const isItemOwnedByCurrentWorkspace =
    item.resource.workspaceId === context.workspace?.id;
  const isItemOwnedByCurrentUser =
    item.resource.creatorId === context.workspaceUser?.workspaceUserId &&
    item.resource.visibility === ResourceVisibilityType.Private;

  const showVisibility = isAdmin && classroom == null;

  const canSetVisibility =
    isItemOwnedByCurrentWorkspace && isAdmin && classroom == null;

  const canDelete =
    (isItemOwnedByCurrentUser || isAdmin) && isItemOwnedByCurrentWorkspace;

  return (
    <>
      <VisibilityModal
        resource={item.resource}
        isOpen={isVisibilityModalOpen}
        onRequestClose={() => setIsVisibilityModalOpen(false)}
      />

      <tr key={item.resource.id}>
        <td>
          <RouterLink to={url ?? "#"}>
            <StyledTooltipWrapper>
              <Tooltip tip={typeLabel}>
                <span>
                  <IconComponent width="12" />
                </span>
              </Tooltip>
            </StyledTooltipWrapper>
            {item.resource.name}
          </RouterLink>
        </td>
        <td>
          {item.resource.visibility === "PRIVATE"
            ? "Me"
            : (item.resource.workspace?.name ?? item.resource.workspaceId)}
        </td>
        <td>
          {item.locations.map((loc) => (
            <div key={loc.id}>{getLocationName(loc)}</div>
          ))}
        </td>
        <ResourceTools>
          <StyledToolbar>
            <Toolbar.Right>
              <FavoriteButton item={item.resource} />
              <PinToPlannerButton
                variant="plain"
                resource={item.resource}
                classroom={classroom}
              />
              {showVisibility && (
                <Button
                  title={
                    isItemOwnedByCurrentWorkspace
                      ? `Visible to ${visibilityLabel.toLowerCase()}`
                      : "Shared with my workspace"
                  }
                  disabled={!canSetVisibility}
                  onClick={() => setIsVisibilityModalOpen(true)}
                  variant="plain"
                >
                  <VisibilityIcon width="12" />
                </Button>
              )}
              <Button
                title="delete"
                variant="plain"
                disabled={!canDelete}
                icon={<IconTrashLine />}
                onClick={async () => {
                  await ConfirmModal.show({
                    onResolving: async (result: boolean) => {
                      if (result) {
                        await deleteResource.mutateAsync({
                          id: item.resource.id,
                          sk: item.resource.sk,
                        });
                      }
                    },
                    children: (
                      <div style={{ padding: "1rem" }}>
                        <CautionBanner variant="plain">
                          Are you sure you want to remove this resource?
                        </CautionBanner>
                      </div>
                    ),
                  });
                }}
              />
            </Toolbar.Right>
          </StyledToolbar>
        </ResourceTools>
      </tr>
    </>
  );
};

const ResourcesTable = (props: { location: ResourceLocation | undefined }) => {
  const win = useContext(WindowContext);
  const [filter, setFilter] = useState("");
  const [isNewLinkModalOpen, setIsNewLinkModalOpen] = useState(false);
  const [isUploadFileModalOpen, setIsUploadFileModalOpen] = useState(false);
  const [context] = useAppContext();

  const {
    data: resources,
    isLoading,
    isRefetching,
    refetch,
  } = useQuerySearchResourcesForLocation(props.location);

  const shareQuery = useQueryCheckSharability(
    resources?.map((r) => ({
      id: r.resource.id,
      sk: r.resource.sk,
    })) ?? [],
  );

  const { course, unit, classroom } = decomposeResourceLocation(props.location);

  const isContentManager = isUserRole(
    context.workspaceUser,
    WorkspaceUserRole.ContentManager,
  );

  const canAddResources =
    context.appLicenseConfig?.userContentBankId != null &&
    (isClassroom(props.location) || isContentManager);

  const filteredResources = useMemo(
    () =>
      sortBy(
        resources
          ?.filter((r) =>
            filterMatches(
              filter,
              ...r.locations.map((l) => getLocationName(l)),
              getBrowseForItem(r.resource).label,
              r.resource.workspace?.name,
              r.resource.name,
            ),
          )
          .filter((r) =>
            shareQuery.data?.find(
              (s) => s.id === r.resource.id && s.sk === r.resource.sk,
            ),
          ),
        (r) => r.resource.name,
      ),
    [filter, resources, shareQuery.data],
  );

  const getNoResultsMessage = () => {
    switch (props.location?.__typename) {
      case "Course":
        return "There are no resources for this course.";
      case "Unit":
        return "There are no resources for this unit / module.";
      case "Classroom":
        return `Curate links to resources you want to associate with this ${
          props.location.resourceUnit == null ? "classroom" : "unit"
        } for easy access.`;
      default:
        return "No resources found.";
    }
  };

  return (
    <div>
      <NewLinkModal
        classroomId={classroom?.id}
        courseId={course?.id}
        unitId={unit?.id}
        isOpen={isNewLinkModalOpen}
        onRequestClose={() => setIsNewLinkModalOpen(false)}
        onComplete={() => {
          setIsNewLinkModalOpen(false);
          refetch();
        }}
      />
      <UploadFileModal
        classroomId={classroom?.id}
        isOpen={isUploadFileModalOpen}
        onRequestClose={() => setIsUploadFileModalOpen(false)}
        onComplete={() => setIsUploadFileModalOpen(false)}
      />
      <VSpace size="2rem">
        <Toolbar>
          <TextInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ flex: "1" }}
            placeholder="Filter Resources"
          />
          <Button
            title="refetch"
            icon={<IconRefreshLine />}
            onClick={() => {
              refetch();
            }}
            variant="outlined"
            isLoading={isLoading || isRefetching}
          />
          {canAddResources && (
            <Button
              title="New Web Link"
              onClick={() => setIsNewLinkModalOpen(true)}
              icon={<IconAddLine />}
              color="create"
              disabled={!!classroom?.isArchived}
            >
              {win.device !== "phone" ? "Web Link" : "Link"}
            </Button>
          )}
          {/* <Button
                title="Upload"
                onClick={() => setIsNewLinkModalOpen(true)}
                icon={<IconAddLine />}
              >
                Upload
              </Button> */}
        </Toolbar>
        <Results>
          {isLoading && <Loading />}
          {!isLoading && !filteredResources && (
            <NoResults icon={<IconDocumentLine />}>
              {getNoResultsMessage()}
            </NoResults>
          )}
          {!!filteredResources?.length && (
            <StyledTable mobile={win.device === "phone"}>
              <thead>
                <tr>
                  <th>Resource</th>
                  <th>Added By</th>
                  <th>Used In</th>
                  <ResourceTools as="th">&nbsp;</ResourceTools>
                </tr>
                <ListTableHeaderLine></ListTableHeaderLine>
              </thead>
              <tbody>
                {filteredResources.map((item) => (
                  <ResourcesTableItem
                    key={item.resource.id}
                    item={item}
                    classroom={classroom}
                    isAdmin={isContentManager}
                  />
                ))}
              </tbody>
            </StyledTable>
          )}
        </Results>
      </VSpace>
    </div>
  );
};

export default ResourcesTable;
